import type { LucideIcon } from "lucide-react";

type KpiAccent = "blue" | "green" | "purple";

// Fixed per-tile mapping (not hashed like the row avatar) — each tile has a stable
// identity, so its icon badge always carries the same accent token.
const KPI_ACCENT_CLASSES: Record<KpiAccent, string> = {
  blue: "bg-accent-blue/[0.12] text-accent-blue-ink",
  green: "bg-accent-green/[0.12] text-accent-green-ink",
  purple: "bg-accent-purple/[0.12] text-accent-purple-ink",
};

export function CertameKpiTile({
  label,
  value,
  icon: Icon,
  accent,
  tone = "neutral",
}: {
  label: string;
  value: string | number;
  icon: LucideIcon;
  accent?: KpiAccent;
  tone?: "neutral" | "warn";
}) {
  const badgeClasses =
    tone === "warn"
      ? "bg-warn/[0.14] text-warn-ink"
      : accent
        ? KPI_ACCENT_CLASSES[accent]
        : "bg-surface-muted text-ink-muted";

  return (
    <div
      className={`flex-1 min-w-[180px] rounded-card border px-5 py-4 ${
        tone === "warn"
          ? "border-warn/[0.42] bg-warn/[0.08]"
          : "border-edge bg-surface"
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-chip ${badgeClasses}`}
        >
          <Icon size={20} strokeWidth={2} />
        </span>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
            {label}
          </p>
          <p
            className={`mt-1 text-2xl font-semibold tabular-nums ${
              tone === "warn" ? "text-warn-ink" : "text-ink"
            }`}
          >
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}
